import { useEffect, useRef, useState } from "react";

export type ActionStatus = "idle" | "running" | "done" | "failed";

/** @returns The status of the latest `run`, which falls back to `"idle"` shortly after it settles. */
export function useActionStatus() {
    const [status, setStatus] = useState<ActionStatus>("idle");
    const [error, setError] = useState<string>();
    const resetTimer = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => clearTimeout(resetTimer.current), []);

    const run = async (action: () => Promise<unknown>, errorMessage: string) => {
        clearTimeout(resetTimer.current);
        setStatus("running");
        let failed = false;
        try {
            await action();
            setStatus("done");
        } catch (e) {
            console.error(errorMessage, e);
            setError(e instanceof Error ? e.message : String(e));
            setStatus("failed");
            failed = true;
        }
        // Long enough to read an error message.
        resetTimer.current = setTimeout(() => setStatus("idle"), failed ? 5000 : 1500);
    };

    return [status, run, error] as const;
}
